/*
 * Configuration and Constants
 * 
 * Centralized configuration file containing:
 * - API endpoints and backend URL configuration
 * - Custom Leaflet map marker configurations with vehicle-specific icons
 * - Application constants and settings
 * 
 * TO EDIT CONFIGURATION:
 * - Backend URL: Modify BACKEND_URL (uses environment variable)
 * - Map markers: Adjust icon sizes and colors in MARKER_CONFIGS
 * - Application settings: Update APP_CONSTANTS as needed
 */

import L from 'leaflet';

// ============================================================================
// API CONFIGURATION
// ============================================================================

// Backend API configuration from environment variables
const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

//
//

// WebSocket URL for real-time updates
const WS_URL = BACKEND_URL.replace('http', 'ws');

// ============================================================================
// CUSTOM ICON CREATORS
// ============================================================================

/**
 * Create custom emoji-based icon for vehicles
 * @param {string} emoji - Emoji to display
 * @param {Array} size - [width, height]
 * @param {boolean} isSelected - Whether this vehicle is selected
 * @param {string} color - Background color
 * @param {number|null} heading - Vehicle heading in degrees (0 = North)
 * @param {number} speed - Vehicle speed in km/h
 * @param {string} vehicleType - Type of vehicle (bus, van, car, etc.)
 * @returns {L.DivIcon} Custom Leaflet div icon
 */
const createEmojiIcon = (emoji, size = [32, 32], isSelected = false, color = '#ffffff', heading = null, speed = 0, vehicleType = null) => {
  const borderColor = isSelected ? '#dc2626' : '#e5e7eb';
  const backgroundColor = isSelected ? '#fef2f2' : color;
  const shadow = isSelected ? '0 4px 20px rgba(220, 38, 38, 0.4)' : '0 2px 8px rgba(0,0,0,0.3)';

  // Show direction arrow for buses and vans at any speed > 0.5 km/h, others need > 1 km/h
  const isBusOrVan = vehicleType === 'bus' || vehicleType === 'van';
  const speedThreshold = isBusOrVan ? 0.5 : 1;
  const showDirection = heading !== null && speed > speedThreshold;
  const rotation = heading !== null ? heading : 0;

  // Calculate arrow position based on heading (place on edge in direction of travel)
  const radius = size[0] / 2 + 10; // Place arrow 10px from edge
  const radians = (rotation - 90) * Math.PI / 180; // -90 to adjust for CSS coordinate system
  const arrowX = radius * Math.cos(radians);
  const arrowY = radius * Math.sin(radians);

  return L.divIcon({
    html: `<div style="
      width: ${size[0]}px;
      height: ${size[1]}px;
      position: relative;
      ${isSelected ? 'animation: selectedPulse 1.5s infinite;' : ''}
    ">
      ${showDirection ? `
        <div style="
          position: absolute;
          left: 50%;
          top: 50%;
          transform: translate(-50%, -50%) translate(${arrowX}px, ${arrowY}px);
          width: 20px;
          height: 20px;
          z-index: 10;
        ">
          <svg width="20" height="20" viewBox="0 0 16 16" style="transform: rotate(${rotation}deg); filter: drop-shadow(0 2px 4px rgba(0,0,0,0.6));">
            <polygon points="8,1 14,14 8,11 2,14" fill="#ef4444" stroke="#dc2626" stroke-width="1"/>
          </svg>
        </div>
      ` : ''}
      <div style="
        font-size: ${size[0] * 0.6}px;
        text-align: center;
        line-height: ${size[1]}px;
        width: ${size[0]}px;
        height: ${size[1]}px;
        border-radius: 50%;
        background: ${backgroundColor};
        border: 3px solid ${borderColor};
        box-shadow: ${shadow};
        display: flex;
        align-items: center;
        justify-content: center;
      ">${emoji}</div>
    </div>
    ${isSelected ? `<style>
      @keyframes selectedPulse {
        0%, 100% { transform: scale(1); }
        50% { transform: scale(1.1); }
      }
    </style>` : ''}`,
    className: `custom-vehicle-icon ${isSelected ? 'selected' : ''}`,
    iconSize: size,
    iconAnchor: [size[0] / 2, size[1] / 2],
    popupAnchor: [0, -size[1] / 2]
  });
};

/**
 * Create user location icon with pulsing animation
 * @returns {L.DivIcon} Animated user location icon
 */
const createUserLocationIcon = () => {
  return L.divIcon({
    html: `<div style="
      font-size: 20px;
      text-align: center;
      line-height: 32px;
      width: 32px;
      height: 32px;
      border-radius: 50%;
      background: #3b82f6;
      border: 3px solid #1d4ed8;
      color: white;
      display: flex;
      align-items: center;
      justify-content: center;
      animation: locationPulse 2s infinite;
    ">📍</div>
    <style>
      @keyframes locationPulse {
        0% { box-shadow: 0 0 0 0 rgba(59, 130, 246, 0.7); }
        70% { box-shadow: 0 0 0 15px rgba(59, 130, 246, 0); }
        100% { box-shadow: 0 0 0 0 rgba(59, 130, 246, 0); }
      }
    </style>`,
    className: 'user-location-icon',
    iconSize: [32, 32],
    iconAnchor: [16, 16],
    popupAnchor: [0, -16]
  });
};

// ============================================================================
// MAP MARKER CONFIGURATION - UPDATED WITH CUSTOM ICONS
// ============================================================================

// Fix for default markers in React Leaflet
delete L.Icon.Default.prototype._getIconUrl;
L.Icon.Default.mergeOptions({
  iconRetinaUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.7.1/images/marker-icon-2x.png',
  iconUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.7.1/images/marker-icon.png',
  shadowUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.7.1/images/marker-shadow.png',
});

// Enhanced mobile-friendly marker configurations with vehicle-specific icons
export const MARKER_CONFIGS = {
  // Garbage truck marker - Blue-teal background (municipal theme)
  BUS: {
    size: [40, 40],
    emoji: '🚛',
    color: '#cffafe', // Light cyan/teal background for garbage trucks
    selectedSize: [48, 48]
  },

  // Private vehicle marker - Blue background with car emoji
  PRIVATE_VEHICLE: {
    size: [35, 35],
    emoji: '🚗',
    color: '#dbeafe', // Light blue background
    selectedSize: [40, 40]
  },

  // Taxi marker - Yellow background with taxi emoji
  TAXI: {
    size: [35, 35],
    emoji: '🚕',
    color: '#fef3c7', // Light yellow background
    selectedSize: [40, 40]
  },

  // Truck marker - Blue-green background (municipal theme)
  TRUCK: {
    size: [40, 40],
    emoji: '🚛',
    color: '#ccfbf1', // Light teal background
    selectedSize: [48, 48]
  },

  // Location picker marker - Blue for alerts
  LOCATION_PICKER: {
    size: [30, 30],
    emoji: '📍',
    color: '#dbeafe', // Light blue background
    selectedSize: [35, 35]
  }
};

// Create Leaflet icon instances for use in components
const MARKERS = {
  busIcon: (isSelected = false, heading = null, speed = 0) => createEmojiIcon(
    MARKER_CONFIGS.BUS.emoji,
    isSelected ? MARKER_CONFIGS.BUS.selectedSize : MARKER_CONFIGS.BUS.size,
    isSelected,
    MARKER_CONFIGS.BUS.color,
    heading,
    speed,
    'bus'
  ),

  vanIcon: (isSelected = false, heading = null, speed = 0) => createEmojiIcon(
    '🚐',
    isSelected ? [40, 40] : [35, 35],
    isSelected,
    '#fed7aa',
    heading,
    speed,
    'van'
  ),

  privateVehicleIcon: (isSelected = false, heading = null, speed = 0) => createEmojiIcon(
    MARKER_CONFIGS.PRIVATE_VEHICLE.emoji,
    isSelected ? MARKER_CONFIGS.PRIVATE_VEHICLE.selectedSize : MARKER_CONFIGS.PRIVATE_VEHICLE.size,
    isSelected,
    MARKER_CONFIGS.PRIVATE_VEHICLE.color,
    heading,
    speed,
    'car'
  ),

  taxiIcon: (isSelected = false, heading = null, speed = 0) => createEmojiIcon(
    MARKER_CONFIGS.TAXI.emoji,
    isSelected ? MARKER_CONFIGS.TAXI.selectedSize : MARKER_CONFIGS.TAXI.size,
    isSelected,
    MARKER_CONFIGS.TAXI.color,
    heading,
    speed,
    'taxi'
  ),

  truckIcon: (isSelected = false, heading = null, speed = 0) => createEmojiIcon(
    MARKER_CONFIGS.TRUCK.emoji,
    isSelected ? MARKER_CONFIGS.TRUCK.selectedSize : MARKER_CONFIGS.TRUCK.size,
    isSelected,
    MARKER_CONFIGS.TRUCK.color,
    heading,
    speed,
    'truck'
  ),

  userLocationIcon: () => createUserLocationIcon(),

  locationPickerIcon: (isSelected = false) => createEmojiIcon(
    MARKER_CONFIGS.LOCATION_PICKER.emoji,
    isSelected ? MARKER_CONFIGS.LOCATION_PICKER.selectedSize : MARKER_CONFIGS.LOCATION_PICKER.size,
    isSelected,
    MARKER_CONFIGS.LOCATION_PICKER.color
  )
};

// ============================================================================
// APPLICATION BRANDING - Clean City Belmopan
// ============================================================================

export const APP_BRANDING = {
  name: 'Clean City Belmopan',
  shortName: 'Clean City Belmopan',
  description: 'Real-time garbage collection tracking for a cleaner Belmopan',
  organization: 'Belmopan City Council',
  tagline: 'A Cleaner Belmopan, Together'
};

// ============================================================================
// APPLICATION CONSTANTS
// ============================================================================

const APP_CONSTANTS = {
  // Map configuration
  MAP: {
    // Belmopan City coordinates for map center (Capital of Belize)
    BELIZE_CENTER: [17.2510, -88.7590],
    DEFAULT_ZOOM: 14,
    SELECTED_ZOOM: 16,
    SUBSCRIPTION_ZOOM: 15
  },

  // Real-time update intervals
  INTERVALS: {
    WEBSOCKET_PING: 30000, // 30 seconds
    AD_DISPLAY: 30000, // 30 seconds
    DATA_REFRESH: 60000 // 1 minute
  },

  // UI configuration
  UI: {
    NOTIFICATION_DISPLAY_COUNT: 3,
    AD_DISPLAY_CHANCE: 0.3, // 30% chance
    ANIMATION_DURATION: 1500 // 1.5 seconds
  },

  // Default form values
  DEFAULTS: {
    VEHICLE: {
      YEAR: new Date().getFullYear(),
      UPDATE_FREQUENCY: 60,
      VEHICLE_TYPE: 'private',
      IS_PUBLIC: false
    },
    SUBSCRIPTION: {
      NOTIFICATION_DISTANCE: 1.0, // miles
      DEPARTURE_TIMES: ['06:00', '07:00', '08:00', '12:00', '16:00', '17:00', '18:00']
    },
    ADVERTISEMENT: {
      AD_TYPE: 'banner',
      TARGET_AUDIENCE: 'all',
      COST_PER_VIEW: 0.01,
      BUDGET: 100,
      DURATION_DAYS: 30
    }
  }
};

// ============================================================================
// TOWNS AND LOCATIONS (For route subscriptions)
// ============================================================================

const BELIZE_TOWNS = [
  'Belize City',
  'San Pedro',
  'Caye Caulker',
  'Orange Walk',
  'Corozal',
  'Dangriga',
  'Placencia',
  'Punta Gorda',
  'San Ignacio',
  'Benque Viejo',
  'Spanish Lookout',
  'Belmopan'
];

// ============================================================================
// UTILITY FUNCTIONS
// ============================================================================

/**
 * Format time since last update for display - FIXED VERSION
 * Properly handles UTC timestamps from backend
 * @param {string|Date} timestamp - The timestamp to format (should be UTC)
 * @returns {string} Formatted time string
 */
const getTimeSinceUpdate = (timestamp) => {
  if (!timestamp) return 'Never';

  const now = new Date();
  let updateTime;

  if (typeof timestamp === 'string') {
    // Handle different timestamp formats
    if (timestamp.endsWith('Z')) {
      updateTime = new Date(timestamp);
    } else if (timestamp.includes('+00:00')) {
      // Handle timezone offset format like "2025-08-18T20:36:24.122816+00:00"
      updateTime = new Date(timestamp);
    } else if (timestamp.includes('T')) {
      // ISO string without timezone - assume UTC
      updateTime = new Date(timestamp + 'Z');
    } else {
      // Fallback for other formats
      updateTime = new Date(timestamp);
    }
  } else {
    // Handle Date objects
    updateTime = new Date(timestamp);
  }

  // Check if date is valid
  if (isNaN(updateTime.getTime())) {
    return 'Invalid time';
  }

  // Calculate difference in minutes
  const diffMilliseconds = now.getTime() - updateTime.getTime();
  const diffMinutes = Math.floor(diffMilliseconds / (1000 * 60));

  // Debug logging (remove after testing)
  // if (diffMinutes < 0 || diffMinutes > 1440) { // Negative or more than 24 hours
  //
  // }

  // Handle negative values (future timestamps)
  if (diffMinutes < 0) {
    const futureDiff = Math.abs(diffMinutes);
    if (futureDiff < 60) {
      return `In ${futureDiff} mins`;
    } else {
      const futureHours = Math.floor(futureDiff / 60);
      return `In ${futureHours} hours`;
    }
  }

  // Normal case - past timestamps
  if (diffMinutes === 0) return 'Just now';
  if (diffMinutes === 1) return '1 min ago';
  if (diffMinutes < 60) return `${diffMinutes} mins ago`;

  const diffHours = Math.floor(diffMinutes / 60);
  if (diffHours === 1) return '1 hour ago';
  if (diffHours < 24) return `${diffHours} hours ago`;

  const diffDays = Math.floor(diffHours / 24);
  if (diffDays === 1) return '1 day ago';
  return `${diffDays} days ago`;
};


/**
 * Get appropriate marker icon based on vehicle type and selection state
 * @param {Object} vehicle - Vehicle object
 * @param {string} selectedVehicleId - Currently selected vehicle ID
 * @returns {L.DivIcon} Custom Leaflet icon instance
 */
const getVehicleMarkerIcon = (vehicle, selectedVehicleId) => {
  const isSelected = vehicle.id === selectedVehicleId;
  const heading = vehicle?.last_location?.heading;
  const speed = vehicle?.last_location?.speed || 0;

  // Determine vehicle type and return appropriate icon
  switch (vehicle.vehicle_type?.toLowerCase()) {
    case 'bus':
      return MARKERS.busIcon(isSelected, heading, speed);

    case 'van':
      return MARKERS.vanIcon(isSelected, heading, speed);

    case 'taxi':
      return MARKERS.taxiIcon(isSelected, heading, speed);

    case 'truck':
    case 'delivery':
      return MARKERS.truckIcon(isSelected, heading, speed);

    case 'private':
    case 'car':
    default:
      return MARKERS.privateVehicleIcon(isSelected, heading, speed);
  }
};

/**
 * Get user location icon
 * @returns {L.DivIcon} User location icon with pulsing animation
 */
const getUserLocationIcon = () => {
  return MARKERS.userLocationIcon();
};

/**
 * Format currency for display
 * @param {number} amount - Amount to format
 * @returns {string} Formatted currency string
 */
export const formatCurrency = (amount) => {
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD'
  }).format(amount);
};

/**
 * Generate unique ID for forms and components
 * @returns {string} Unique identifier
 */
export const generateId = () => {
  return Math.random().toString(36).substr(2, 9);
};

/**
 * Validate email format
 * @param {string} email - Email to validate
 * @returns {boolean} Whether email is valid
 */
export const isValidEmail = (email) => {
  const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
  return emailRegex.test(email);
};

/**
 * Get current location using browser geolocation API
 * Enhanced for iOS Safari compatibility
 * @returns {Promise<{lat: number, lng: number}>} Current location coordinates
 */
const getCurrentLocation = () => {
  return new Promise((resolve, reject) => {
    if (!navigator.geolocation) {
      reject(new Error('Geolocation is not supported by this browser.'));
      return;
    }

    // Detect iOS for platform-specific options
    const isIOS = /iPad|iPhone|iPod/.test(navigator.userAgent) && !window.MSStream;

    // iOS-optimized options: Use lower accuracy first, fallback to high accuracy
    const options = {
      timeout: isIOS ? 15000 : 10000, // Longer timeout for iOS
      enableHighAccuracy: false, // Start with low accuracy for faster response
      maximumAge: 30000 // Allow cached position up to 30 seconds old
    };

    // First attempt with low accuracy (faster on iOS)
    navigator.geolocation.getCurrentPosition(
      (position) => {
        resolve({
          lat: position.coords.latitude,
          lng: position.coords.longitude,
          accuracy: position.coords.accuracy
        });
      },
      (error) => {
        // If first attempt fails, try with high accuracy
        if (isIOS) {
          // iOS: try one more time with different settings
          navigator.geolocation.getCurrentPosition(
            (position) => {
              resolve({
                lat: position.coords.latitude,
                lng: position.coords.longitude,
                accuracy: position.coords.accuracy
              });
            },
            (finalError) => {
              // All attempts failed - provide detailed error
              let errorMessage = 'Unable to get your location. ';

              switch (finalError.code) {
                case finalError.PERMISSION_DENIED:
                  errorMessage += 'Please enable location permissions in Settings → Safari → Location.';
                  break;
                case finalError.POSITION_UNAVAILABLE:
                  errorMessage += 'Location information unavailable.';
                  break;
                case finalError.TIMEOUT:
                  errorMessage += 'Location request timed out. Please try again.';
                  break;
                default:
                  errorMessage += 'An unknown error occurred.';
              }

              reject(new Error(errorMessage));
            },
            {
              timeout: 20000,
              enableHighAccuracy: true,
              maximumAge: 10000
            }
          );
        } else {
          // Non-iOS: reject with error details
          let errorMessage = 'Unable to get your location. ';

          switch (error.code) {
            case error.PERMISSION_DENIED:
              errorMessage += 'Please enable location permissions.';
              break;
            case error.POSITION_UNAVAILABLE:
              errorMessage += 'Location information unavailable.';
              break;
            case error.TIMEOUT:
              errorMessage += 'Request timed out.';
              break;
            default:
              errorMessage += 'An unknown error occurred.';
          }

          reject(new Error(errorMessage));
        }
      },
      options
    );
  });
};

// Export utility functions and constants
export {
  API,
  WS_URL,

  APP_CONSTANTS,
  BELIZE_TOWNS,
  getCurrentLocation,
  getTimeSinceUpdate,
  getVehicleMarkerIcon,
  getUserLocationIcon,
  MARKERS
};